/**
 * One styled tooltip for the whole game.
 *
 * Mounted once at the app root, this listens at the document level and takes over any element that
 * carries a `title` (or an explicit `data-tip`). That is deliberate: there are ~370 `title`
 * attributes spread over ~180 files, and rewriting them would have meant editing every call site —
 * risky, because plenty of the `title=` in this codebase are React props on components
 * (`<Modal title=…>`, `<Card title=…>`) that never become DOM attributes at all. A delegated
 * listener only ever sees real DOM attributes, so it upgrades exactly the right ones and nothing
 * else, and any `title` added later is styled without anyone having to remember this file exists.
 *
 * The native tooltip cannot be styled or suppressed with CSS, so the `title` is lifted off the
 * element while the styled one is up and put back when it leaves. React does not know we did that,
 * which is fine: it only re-sets the attribute if the value itself changes, and we restore it on
 * the way out either way.
 *
 * Opt-in extras, for markup that wants them:
 *   data-tip="…"            text to show instead of `title`
 *   data-tip-title="…"      a heading line above the text
 *   data-tip-placement=…    "top" | "bottom" (otherwise top, flipping when there is no room)
 *   data-tip-hide           suppresses the tooltip on this element and its children
 */

import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import './tooltip.css'

/** Hover intent. Long enough not to fire while the pointer crosses a toolbar, far below the ~1s OS delay. */
const SHOW_DELAY_MS = 260
/** Gap between the trigger and the panel, leaving room for the arrow. */
const OFFSET_PX = 10
/** Keeps the panel off the very edge of the viewport. */
const VIEWPORT_MARGIN_PX = 8
/** How far the arrow may sit from the panel's own corner. */
const ARROW_INSET_PX = 14

type Placement = 'top' | 'bottom'

interface ActiveTip {
  el: HTMLElement
  title?: string
  text: string
  requested: Placement | null
}

interface Position {
  left: number
  top: number
  placement: Placement
  arrowLeft: number
}

/** Original `title` values, held off-DOM so the attribute can be restored exactly as it was. */
const stashedTitles = new WeakMap<HTMLElement, string>()

/** The text an element wants to show, or null if it wants nothing. */
function readTipText(el: HTMLElement): string | null {
  const explicit = el.getAttribute('data-tip')
  if (explicit !== null) return explicit.trim() || null
  const title = el.getAttribute('title') ?? stashedTitles.get(el)
  return title?.trim() || null
}

function readPlacement(el: HTMLElement): Placement | null {
  const requested = el.getAttribute('data-tip-placement')
  return requested === 'top' || requested === 'bottom' ? requested : null
}

/**
 * Places the panel against its trigger: above by default, below when there is no room above, always
 * within the viewport, with the arrow tracking the trigger's centre even after the panel is nudged
 * sideways to fit.
 */
function place(rect: DOMRect, tipW: number, tipH: number, requested: Placement | null): Position {
  const vw = window.innerWidth
  const vh = window.innerHeight

  const fitsAbove = rect.top - tipH - OFFSET_PX >= VIEWPORT_MARGIN_PX
  const fitsBelow = rect.bottom + tipH + OFFSET_PX <= vh - VIEWPORT_MARGIN_PX
  let placement: Placement
  if (requested === 'bottom') placement = fitsBelow || !fitsAbove ? 'bottom' : 'top'
  else if (requested === 'top') placement = fitsAbove || !fitsBelow ? 'top' : 'bottom'
  else placement = fitsAbove || !fitsBelow ? 'top' : 'bottom'

  const top = placement === 'top' ? rect.top - tipH - OFFSET_PX : rect.bottom + OFFSET_PX

  const anchorX = rect.left + rect.width / 2
  const maxLeft = Math.max(VIEWPORT_MARGIN_PX, vw - tipW - VIEWPORT_MARGIN_PX)
  const left = Math.min(Math.max(anchorX - tipW / 2, VIEWPORT_MARGIN_PX), maxLeft)

  // The arrow stays under the trigger, not under the middle of a panel that had to shift to fit.
  const arrowLeft = Math.min(
    Math.max(anchorX - left, ARROW_INSET_PX),
    Math.max(ARROW_INSET_PX, tipW - ARROW_INSET_PX),
  )

  return { left, top: Math.max(VIEWPORT_MARGIN_PX, top), placement, arrowLeft }
}

export const GlobalTooltipHost: React.FC = () => {
  const [tip, setTip] = useState<ActiveTip | null>(null)
  const [pos, setPos] = useState<Position | null>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const showTimer = useRef<number | null>(null)
  /**
   * The element currently showing a tooltip, mirrored into a ref so the listener effect does not
   * depend on `tip` — otherwise all eight document listeners are torn down and re-registered on
   * every hover.
   */
  const activeElRef = useRef<HTMLElement | null>(null)
  const tipId = `gt-${useId().replace(/:/g, '')}`

  const cancelPending = useCallback(() => {
    if (showTimer.current !== null) {
      clearTimeout(showTimer.current)
      showTimer.current = null
    }
  }, [])

  /** Puts the element back exactly as it was found. */
  const release = useCallback((active: ActiveTip | null) => {
    if (!active) return
    const { el } = active
    const stashed = stashedTitles.get(el)
    if (stashed !== undefined) {
      el.setAttribute('title', stashed)
      stashedTitles.delete(el)
    }
    if (el.getAttribute('aria-describedby')?.startsWith('gt-')) {
      el.removeAttribute('aria-describedby')
    }
  }, [])

  const hide = useCallback(() => {
    cancelPending()
    activeElRef.current = null
    setTip((current) => {
      release(current)
      return null
    })
    setPos(null)
  }, [cancelPending, release])

  const show = useCallback(
    (el: HTMLElement) => {
      const text = readTipText(el)
      if (!text) return

      // Lift the native tooltip out of the way; it cannot be styled or suppressed otherwise.
      const title = el.getAttribute('title')
      if (title !== null) {
        stashedTitles.set(el, title)
        el.removeAttribute('title')
      }
      el.setAttribute('aria-describedby', tipId)
      activeElRef.current = el
      setPos(null)
      setTip({ el, title: title ?? undefined, text, requested: readPlacement(el) })
    },
    [tipId],
  )

  useEffect(() => {
    const findTrigger = (target: EventTarget | null): HTMLElement | null => {
      if (!(target instanceof Element)) return null
      const el = target.closest<HTMLElement>('[data-tip], [title]')
      if (!el) return null
      if (el.closest('[data-tip-hide]')) return null
      return readTipText(el) ? el : null
    }

    const onPointerOver = (e: PointerEvent) => {
      // Touch has no hover; a tooltip there would be a panel the player cannot dismiss.
      if (e.pointerType === 'touch') return
      const el = findTrigger(e.target)
      if (!el) return
      if (activeElRef.current === el) return
      cancelPending()
      showTimer.current = window.setTimeout(() => show(el), SHOW_DELAY_MS)
    }

    /**
     * Leaving the trigger hides the tooltip at once.
     *
     * Resolved against the element that currently owns the tooltip, NOT by re-running the selector:
     * `show` lifts the `title` attribute off the trigger (that is how the native tooltip is
     * suppressed), so while a tooltip is up its own trigger no longer matches `[title]`. Looking it
     * up again returned null, this handler bailed out, and the tooltip hung around until a scroll,
     * a click, Escape, or a different trigger happened to clear it.
     */
    const onPointerOut = (e: PointerEvent) => {
      const to = e.relatedTarget
      const active = activeElRef.current

      if (active) {
        // Only care about the pointer leaving the trigger that owns the tooltip.
        if (!(e.target instanceof Node) || !active.contains(e.target)) return
        // Moving between the trigger's own children is not leaving it.
        if (to instanceof Node && active.contains(to)) return
        hide()
        return
      }

      // Nothing shown yet: leaving a trigger should call off its pending tooltip.
      const el = findTrigger(e.target)
      if (!el) return
      if (to instanceof Node && el.contains(to)) return
      cancelPending()
    }

    // Keyboard users get the same tooltip, without the hover delay.
    const onFocusIn = (e: FocusEvent) => {
      const el = findTrigger(e.target)
      if (!el) return
      cancelPending()
      show(el)
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide()
    }

    document.addEventListener('pointerover', onPointerOver, true)
    document.addEventListener('pointerout', onPointerOut, true)
    document.addEventListener('focusin', onFocusIn, true)
    document.addEventListener('focusout', hide, true)
    // A tooltip pinned to a rect is wrong the moment anything moves it.
    document.addEventListener('pointerdown', hide, true)
    /*
     * The pointer leaving the window (or the browser losing focus) fires no `pointerout` on the
     * trigger, so without these a tooltip could be left on screen after the cursor is long gone.
     */
    document.addEventListener('mouseleave', hide)
    window.addEventListener('blur', hide)
    window.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide)
    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('pointerover', onPointerOver, true)
      document.removeEventListener('pointerout', onPointerOut, true)
      document.removeEventListener('focusin', onFocusIn, true)
      document.removeEventListener('focusout', hide, true)
      document.removeEventListener('pointerdown', hide, true)
      document.removeEventListener('mouseleave', hide)
      window.removeEventListener('blur', hide)
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide)
      window.removeEventListener('keydown', onKeyDown)
    }
    // No `tip` here on purpose — the listeners are registered once for the life of the host.
  }, [cancelPending, hide, show])

  // Measure once mounted, then place it. Until `pos` is set the panel is rendered but transparent,
  // so it is never seen in the wrong spot.
  useLayoutEffect(() => {
    if (!tip || !tipRef.current) return
    const el = tipRef.current
    const rect = tip.el.getBoundingClientRect()
    // A trigger that has been unmounted or hidden has nothing to point at.
    if (rect.width === 0 && rect.height === 0) {
      hide()
      return
    }
    setPos(place(rect, el.offsetWidth, el.offsetHeight, tip.requested))
  }, [tip, hide])

  // Whatever happens, do not leave an element stripped of its title.
  useEffect(() => () => release(tip), [tip, release])

  if (!tip) return null

  const heading = tip.el.getAttribute('data-tip-title')

  return createPortal(
    <div
      ref={tipRef}
      id={tipId}
      role="tooltip"
      className={`gt-tip gt-${pos?.placement ?? 'top'}${pos ? ' gt-visible' : ''}`}
      style={
        {
          left: pos?.left ?? 0,
          top: pos?.top ?? 0,
          '--gt-arrow-left': `${pos?.arrowLeft ?? 0}px`,
        } as React.CSSProperties
      }
    >
      {heading && <span className="gt-tip-title">{heading}</span>}
      {tip.text}
    </div>,
    document.body,
  )
}

export default GlobalTooltipHost
